import { Card } from '@siliconx/shared/ui/Card';
import { ENV_AVOIDED_TILE_MICROCOPY } from '@siliconx/shared';
import { useKpiAvoided, useKpiScope2, useKpiScope3, useKpiTotal } from '../../hooks/useEnvironmentalOverview';
import { BasisTag } from './BasisTag';
import { formatTonnes } from './format';

/**
 * SIL-472 Overview KPI strip. Four tiles:
 *   TOTAL / SCOPE 2 / SCOPE 3 — REAL, aggregated server-side from
 *   v_asset_use_phase_latest + v_asset_carbon_footprint_latest.
 *   AVOIDED — HYBRID (SIL-471): real for an org with at least one COMPLETED
 *   `marketplace_transaction`, SAMPLE fallback otherwise (`useKpiAvoided`) —
 *   either way, SIL-459 hard-quarantined: a visual divider before it, the
 *   sky color family (never the official tiles' green/zinc), and the
 *   mandatory on-tile microcopy. This tile is NEVER summed with, netted
 *   against, or drawn on the same axis as TOTAL/SCOPE2/SCOPE3.
 *
 * SIL-488 (sign-off decision): the INTENSITY (kg/PF-day) tile is DROPPED —
 * no serving surface (only 35/123 fleet assets resolved both inputs), and
 * the metric is gpu-cohort-only meaning that doesn't generalize across a
 * mixed fleet. Revisit only when gpu-cohort coverage justifies a defined
 * formula (spec §8 deferred register).
 *
 * Deliberately does NOT build the mock's "Gross inventory footprint" bar
 * (NET + AVOIDED summed) or any "% displaced" ratio — banned per the
 * SIL-459 design-deviation register.
 */
export function KpiStrip() {
  const total = useKpiTotal();
  const scope2 = useKpiScope2();
  const scope3 = useKpiScope3();
  const avoided = useKpiAvoided();

  return (
    <Card padding="md" className="flex flex-wrap items-stretch overflow-hidden p-0" data-testid="kpi-strip">
      <KpiTile
        label="TOTAL"
        value={total.isLoading ? '…' : formatTonnes(total.data)}
        basis={total.basis}
        border
      />
      <KpiTile
        label="SCOPE 2"
        value={scope2.isLoading ? '…' : formatTonnes(scope2.data)}
        basis={scope2.basis}
        accentClassName="text-indigo-700"
        border
      />
      <KpiTile
        label="SCOPE 3"
        value={scope3.isLoading ? '…' : formatTonnes(scope3.data)}
        basis={scope3.basis}
        accentClassName="text-emerald-700"
        border
      />

      {/* SIL-459: mandatory visual divider before the quarantined AVOIDED
       * tile — a thicker, dashed separator so a screenshot still shows the
       * seam even cropped tight. */}
      <div className="w-0 shrink-0 self-stretch border-l-2 border-dashed border-sky-300" aria-hidden="true" />

      <KpiTile
        label="AVOIDED"
        value={avoided.isLoading ? '…' : formatTonnes(avoided.data.valueKgco2e)}
        basis={avoided.basis}
        accentClassName="text-sky-700"
        toneClassName="bg-sky-50/60"
        microcopy={ENV_AVOIDED_TILE_MICROCOPY}
        detail={`${avoided.data.transferCount.toLocaleString('en-US')} XFERS`}
      />
    </Card>
  );
}

function KpiTile({
  label,
  value,
  basis,
  detail,
  microcopy,
  accentClassName = 'text-zinc-900',
  toneClassName,
  border,
}: {
  label: string;
  value: string;
  basis: ReturnType<typeof useKpiTotal>['basis'] | ReturnType<typeof useKpiAvoided>['basis'];
  detail?: string;
  microcopy?: string;
  accentClassName?: string;
  toneClassName?: string;
  border?: boolean;
}) {
  return (
    <div
      className={`flex min-w-[150px] flex-1 flex-col gap-1 px-5 py-4 ${border ? 'border-r border-zinc-200' : ''} ${toneClassName ?? ''}`}
    >
      <div className="flex items-center justify-between gap-2">
        <span className="text-xs font-semibold uppercase tracking-widest text-zinc-500">{label}</span>
        <BasisTag basis={basis} />
      </div>
      <span className={`font-mono text-xl font-semibold tabular-nums ${accentClassName}`}>{value}</span>
      {detail && <span className="text-xs text-zinc-500">{detail}</span>}
      {microcopy && <span className="text-xs font-medium text-sky-700">{microcopy}</span>}
    </div>
  );
}
